import {assert} from 'Asserts';
import {QueryResultType, SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPrimaryRegistryIdentifier} from 'registry/RegistryIdentifierUtil';
import APIUtil from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';

import {
  PrintBarcodeModalLabelParamsQuery,
  PrintBarcodeModalLabelParamsQueryVariables,
} from './__generated/PrintBarcodeModalLabelParamsQuery';
import {PrintBarcodeModalQuery} from './__generated/PrintBarcodeModalQuery';

export class NotPrintableError extends ErrorSubclass {}

export interface PrintableExtras {
  id: string;
  barcodeOrName: string;
  schemaId: string;
  schemaRegistryId: string;
  // Used only by the BarTender print flow
  labelParamsJson?: JSONScalar;
}

export type PrintableBioentity = ExcludeInaccessible<PrintBarcodeModalQuery['bioentities'][number]>;

export type PrintableStorable = ExcludeInaccessible<PrintBarcodeModalQuery['storables'][number]>;

export type LabelPrintable = (PrintableBioentity | PrintableStorable) & PrintableExtras;

export function isPrintableContainer(
  item: LabelPrintable
): item is ExtractByTypename<PrintableStorable, 'ContainerNode'> & PrintableExtras {
  return item.__typename === 'ContainerNode';
}

export function storableLabelPrintable(
  storable: PrintableStorable,
  labelParamsJson?: JSONScalar
): LabelPrintable {
  return {
    ...storable,
    barcodeOrName: storable.barcode?.value ?? storable.name,
    schemaId: storable.storableSchema.id,
    schemaRegistryId: storable.storableSchema.registry.id,
    labelParamsJson,
  };
}

export function bioentityLabelPrintable(
  bioentity: PrintableBioentity,
  labelParamsJson?: JSONScalar
): LabelPrintable {
  const primaryRegistryId = getPrimaryRegistryIdentifier(bioentity);

  if (!primaryRegistryId) {
    throw new NotPrintableError('Bioentities to be printed should be registered');
  }
  if (!bioentity.tagSchema) {
    throw new NotPrintableError('Registered bioentities should have a schema');
  }

  return {
    ...bioentity,
    barcodeOrName: primaryRegistryId,
    schemaId: bioentity.tagSchema.id,
    schemaRegistryId: bioentity.tagSchema.registry.id,
    labelParamsJson,
  };
}

function buildLabelParamsByItemId(
  labelParamsResult: SkippableBenchlingQueryResult<
    PrintBarcodeModalLabelParamsQuery,
    PrintBarcodeModalLabelParamsQueryVariables
  >
): Record<string, JSONScalar> {
  if (labelParamsResult.type !== QueryResultType.SUCCESS) {
    return {};
  }

  const labelParamsByItemId: Record<string, JSONScalar> = {};
  for (const items of [labelParamsResult.data.bioentities, labelParamsResult.data.storables]) {
    for (const item of items) {
      if (!isInaccessible(item)) {
        labelParamsByItemId[item.id] = item.labelParamsJson;
      }
    }
  }
  return labelParamsByItemId;
}

export function buildPrintableItems(
  bioentities: PrintBarcodeModalQuery['bioentities'],
  storables: PrintBarcodeModalQuery['storables'],
  labelParamsResult: SkippableBenchlingQueryResult<
    PrintBarcodeModalLabelParamsQuery,
    PrintBarcodeModalLabelParamsQueryVariables
  >
): Array<LabelPrintable> {
  const labelParamsByItemId = buildLabelParamsByItemId(labelParamsResult);

  return [
    ...bioentities.flatMap((bioentity) =>
      isInaccessible(bioentity) ? [] : [bioentityLabelPrintable(bioentity, labelParamsByItemId[bioentity.id])]
    ),
    ...storables.flatMap((storable) =>
      isInaccessible(storable) ? [] : [storableLabelPrintable(storable, labelParamsByItemId[storable.id])]
    ),
  ];
}

// Keep in sync with benchling/label_printing/label_templates.py:label_printable_type
export function labelPrintableType(itemId: string): string {
  if (APIUtil.isStorable(itemId)) {
    return 'STORABLE';
  } else if (APIUtil.isBiologicalEntity(itemId)) {
    return 'BIOENTITY';
  }
  assert(false, 'Invalid Printable Item Type');
  return null as never;
}
